import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateTrackingType } from '@/lib/utils';
import axios from 'axios';
import { API_URL } from '@/lib/api';
import { Vitrine, FaixaBeuni } from '@/components/project/Vitrine';
import Rodape from '@/components/project/Rodape';
import ResumoDoRastreio from '@/components/project/ResumoDoRastreio';
import Cabecalho from '@/components/project/Cabecalho';
import { TrackingData } from '@/lib/types';
import Historico from '@/components/project/Historico';
import { Carregando, ErroDoRastreio, TipoDeErro } from '@/components/project/EstadosDoRastreio';


export default function TrackPage() {
  const { trackingCode } = useParams<{ trackingCode: string }>();
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<TipoDeErro | null>(null);
  const navigate = useNavigate();
  // O código da tela agora: resposta que chega depois de a pessoa trocar de
  // código (uma atualização automática atrasada, por exemplo) é descartada.
  const codigoNaTela = useRef(trackingCode);
  codigoNaTela.current = trackingCode;
  // Só vale a resposta da consulta mais nova: uma atualização automática
  // lenta que chega depois de outra não volta a tela pra trás ("Entregue" →
  // "A caminho"). E não sai uma automática enquanto outra está no ar.
  const ultimaConsulta = useRef(0);
  const consultando = useRef(false);

  // `silencioso`: a atualização da entrega ao vivo, por trás, sem trocar a
  // tela pelo carregando nem mostrar erro de uma consulta que falhou no meio.
  const fetchTrackingData = async (code: string, silencioso = false) => {
    if (!code) return;
    if (silencioso && consultando.current) return;
    const minha = ++ultimaConsulta.current;
    const vale = () => minha === ultimaConsulta.current && code === codigoNaTela.current;
    consultando.current = true;

    if (!silencioso) {
      setError(null);
      setLoading(true);
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/track`, {
        params: {
          trackingCode: code,
          trackingType: validateTrackingType(code),
        },
      });
      if (!vale()) return;
      setTrackingData(data);
    } catch (error) {
      if (silencioso || !vale()) return;
      console.error(error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          setError('nao_encontrado');
        } else if (error.response?.status === 400) {
          setError('invalido');
        } else {
          setError('falha');
        }
      } else {
        setError('falha');
      }
    } finally {
      if (minha === ultimaConsulta.current) consultando.current = false;
      if (!silencioso && vale()) setLoading(false);
    }
  };

  // Entrega ao vivo em andamento: consulta de novo a cada 30 s. Aberta com a
  // entrega agendada, o mapa aparece sozinho quando o motorista sai; e o
  // "Entregue" chega sem a pessoa recarregar. Para quando a entrega fecha.
  const acompanhando = Boolean(trackingData?.aoVivo) && ![3, 8].includes(trackingData?.lastStatus ?? 0);
  useEffect(() => {
    if (!acompanhando || !trackingCode) return;
    const id = window.setInterval(() => fetchTrackingData(trackingCode, true), 30000);
    // Resposta velha que chegue depois do fim do acompanhamento já é
    // descartada pela regra da consulta mais nova (não precisa invalidar
    // aqui: isso derrubava a busca manual que limpa os dados).
    return () => window.clearInterval(id);
  }, [acompanhando, trackingCode]);

  // Título da aba com o código (quem tem vários rastreios abertos acha o seu).
  useEffect(() => {
    document.title = trackingCode ? `Rastreio ${trackingCode} · betracking` : 'betracking · rastreio de encomendas';
  }, [trackingCode]);

  useEffect(() => {
    if (trackingCode) {
      setTrackingData(null);
      fetchTrackingData(trackingCode);
    }
  }, [trackingCode]);

  const buscar = (novo: string) => {
    if (novo !== trackingCode) {
      // Código novo: muda a rota (o useEffect busca).
      navigate(`/track/${novo}`);
    } else {
      // O mesmo código: busca de novo sem navegar.
      setTrackingData(null);
      fetchTrackingData(novo);
    }
  };

  const temDados = !loading && !error && trackingData && Array.isArray(trackingData.events);

  return (
    <div className="flex min-h-screen flex-col bg-[#EDE4DA]">
      {/* No erro a busca grande já está no cartão: o menu não repete. */}
      <Cabecalho codigo={trackingCode} onBuscar={error ? undefined : buscar} />
      {/* Página inteira: o rastreio à esquerda e a vitrine da beuni à direita,
          acompanhando a rolagem (no celular ela desce pra depois do
          histórico). Antes do rodapé, a faixa da beuni de largura inteira. */}
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 sm:py-10">
        {loading && <Carregando />}

        {error && <ErroDoRastreio tipo={error} codigo={trackingCode} onBuscar={buscar} onTentarDeNovo={() => trackingCode && fetchTrackingData(trackingCode)} />}

        {temDados && (
          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8">
            <div className="min-w-0 space-y-5">
              <ResumoDoRastreio dados={trackingData} />
              <Historico dados={trackingData} />
              <div className="lg:hidden">
                <Vitrine />
              </div>
            </div>
            <aside className="sticky top-6 hidden lg:block">
              <Vitrine />
            </aside>
          </div>
        )}

      </main>

      {(temDados || error) && <FaixaBeuni />}

      <Rodape />
    </div>
  );
}
